import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import CreditIcon from '../CreditIcon';
import '../../styles/FactionRosterExperience.css';

const toFiniteNumber = (value) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
};

const resolveSwapSavedPoints = (swapData = {}) => {
  const directSavedPoints =
    toFiniteNumber(swapData.sumPointsOut) ??
    toFiniteNumber(swapData.totalPointsOut);

  if (directSavedPoints !== null) {
    return Math.max(directSavedPoints, 0);
  }

  const accumulatedPointsOut = toFiniteNumber(swapData.accumulatedPointsOut);
  const scoreOut = toFiniteNumber(swapData.scoreOut);

  if (accumulatedPointsOut !== null && scoreOut !== null) {
    return Math.max(accumulatedPointsOut + scoreOut, 0);
  }

  return scoreOut;
};

const SwapResume = ({ factionId }) => {
  const [swaps, setSwaps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sumPoints, setSumPoints] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    const fetchSwaps = async () => {
      try {
        let storedSumPoints = null;
        const factionDoc = await getDoc(doc(db, 'factions', factionId));
        if (factionDoc.exists()) {
          storedSumPoints = toFiniteNumber(factionDoc.data().sumPoints);
        }

        const swapsRef = collection(db, 'factions', factionId, 'swaps');
        const querySnapshot = await getDocs(swapsRef);
        const allSwaps = [];

        for (const docSnap of querySnapshot.docs) {
          const data = docSnap.data();

          if (data.swaps && Array.isArray(data.swaps)) {
            const multiSwaps = await Promise.all(
              data.swaps.map(async (swapItem) => {
                const wrestlerOutData = swapItem.wrestlerOut
                  ? (await getDoc(swapItem.wrestlerOut)).data()
                  : null;
                const wrestlerInData = swapItem.wrestlerIn
                  ? (await getDoc(swapItem.wrestlerIn)).data()
                  : null;

                return {
                  id: `${docSnap.id}-${swapItem.wrestlerOut?.id || 'null'}-${swapItem.wrestlerIn?.id || 'null'}`,
                  date: data.date?.toDate() ?? null,
                  wrestlerOut: wrestlerOutData,
                  wrestlerOutId: swapItem.wrestlerOut?.id || null,
                  scoreOut: resolveSwapSavedPoints(swapItem),
                  priceOut: swapItem.priceOut ?? null,
                  wrestlerIn: wrestlerInData,
                  wrestlerInId: swapItem.wrestlerIn?.id || null,
                  priceIn: swapItem.priceIn ?? null,
                  operationType: 'swap',
                  freeSwap: data.freeSwap || false,
                  isMultiSwap: true,
                  swapGroupId: docSnap.id
                };
              })
            );
            allSwaps.push(...multiSwaps);
          } else {
            const wrestlerOutData = data.wrestlerOut
              ? (await getDoc(data.wrestlerOut)).data()
              : null;
            const wrestlerInData = data.wrestlerIn
              ? (await getDoc(data.wrestlerIn)).data()
              : null;

            let operationType = data.operationType || 'swap';
            if (!data.operationType) {
              const hasWrestlerOut = data.wrestlerOut !== null && data.wrestlerOut !== undefined;
              const hasWrestlerIn = data.wrestlerIn !== null && data.wrestlerIn !== undefined;

              if (hasWrestlerOut && !hasWrestlerIn) {
                operationType = 'release';
              } else if (!hasWrestlerOut && hasWrestlerIn) {
                operationType = 'purchase';
              } else if (hasWrestlerOut && hasWrestlerIn) {
                operationType = 'swap';
              }
            }

            allSwaps.push({
              id: docSnap.id,
              date: data.date?.toDate() ?? null,
              wrestlerOut: wrestlerOutData,
              wrestlerOutId: data.wrestlerOut?.id || null,
              scoreOut: resolveSwapSavedPoints(data),
              priceOut: data.priceOut ?? null,
              wrestlerIn: wrestlerInData,
              wrestlerInId: data.wrestlerIn?.id || null,
              priceIn: data.priceIn ?? null,
              operationType,
              freeSwap: data.freeSwap || false,
              isMultiSwap: false
            });
          }
        }

        allSwaps.sort((a, b) => {
          if (!a.date) return 1;
          if (!b.date) return -1;
          return b.date - a.date;
        });

        const derivedSumPoints = allSwaps.reduce(
          (total, swap) => total + Math.max(toFiniteNumber(swap.scoreOut) ?? 0, 0),
          0
        );

        setSumPoints(storedSumPoints ?? derivedSumPoints);
        setSwaps(allSwaps);
      } catch (err) {
        console.error('Errore nel caricamento degli scambi:', err);
        setError('Errore nel caricamento della cronologia scambi');
      } finally {
        setLoading(false);
      }
    };

    if (factionId) {
      fetchSwaps();
    }
  }, [factionId]);

  if (loading) {
    return (
      <div className="frx-page">
        <div className="frx-surface rounded-4 p-4">
          <div className="frx-empty">
            <div className="spinner-border text-brand-secondary" role="status" />
            <strong>Caricamento cronologia</strong>
            <div className="frx-helper">Sto recuperando tutte le operazioni registrate per questa fazione.</div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="frx-page">
        <div className="frx-surface rounded-4 p-4">
          <div className="alert alert-danger frx-alert mb-0 d-flex align-items-center gap-2" role="alert">
            <i className="bi bi-exclamation-triangle-fill"></i>
            <span>{error}</span>
          </div>
        </div>
      </div>
    );
  }

  if (swaps.length === 0) {
    return (
      null
    );
  }

  return (
    <div className="frx-page">
      <div className="frx-surface frx-hero rounded-4 overflow-hidden">
        <div
          className="px-4 py-4 d-flex justify-content-between align-items-center gap-3"
          style={{ cursor: 'pointer' }}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div className="frx-hero-copy">
            <h5 className="mb-2 text-white d-flex align-items-center">
              <i className="bi bi-clock-history me-2 text-brand-secondary"></i>
              Cronologia operazioni
            </h5>
          </div>

          <button
            className="btn btn-outline-brand-secondary rounded-pill px-3 py-2 d-inline-flex align-items-center gap-2"
            onClick={(event) => {
              event.stopPropagation();
              setIsExpanded(!isExpanded);
            }}
            type="button"
          >
            <i className={`bi bi-chevron-${isExpanded ? 'up' : 'down'}`}></i>
          </button>
        </div>

        {isExpanded && (
          <>
            <div className="px-3 pb-1">
              <div>
                <div className="frx-summary-card">
                  <div className="frx-summary-icon">
                    <i className="bi bi-bar-chart-line"></i>
                  </div>
                  <div>
                    <span className="frx-label">SumPoints</span>
                    <div className="frx-value text-brand-secondary">
                      {sumPoints !== null ? sumPoints : '-'}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="px-4 pb-4">
              <div className="frx-table-wrap">
                <div className="table-responsive">
                  <table className="table table-dark mb-0">
                    <thead>
                      <tr>
                        <th className="ps-3 border-0 text-white-50 fw-semibold">Data</th>
                        <th className="border-0 text-white-50 fw-semibold">Tipo</th>
                        <th className="border-0 text-white-50 fw-semibold">Wrestler Svincolato</th>
                        <th className="border-0 text-center text-white-50 fw-semibold">Punti</th>
                        <th className="border-0 text-center text-white-50 fw-semibold">Prezzo Svincolo</th>
                        <th className="border-0 text-white-50 fw-semibold">Wrestler Acquistato</th>
                        <th className="border-0 pe-3 text-center text-white-50 fw-semibold">Prezzo Acquisto</th>
                      </tr>
                    </thead>
                    <tbody>
                      {swaps.map((swap, index) => (
                        <tr
                          key={swap.id}
                          className={swap.isMultiSwap ? 'table-active' : ''}
                          style={{ background: index % 2 === 0 ? 'rgba(255,255,255,0.015)' : 'rgba(255,255,255,0.04)' }}
                        >
                          <td className="ps-3">
                            <div className="d-flex flex-column" style={{ fontSize: '0.9rem' }}>
                              {swap.date ? (
                                <>
                                  <span className="fw-medium text-white">
                                    {swap.date.toLocaleDateString('it-IT', {
                                      day: '2-digit',
                                      month: '2-digit',
                                      year: 'numeric'
                                    })}
                                  </span>
                                  <small className="text-white-50">
                                    {swap.date.toLocaleTimeString('it-IT', {
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    })}
                                  </small>
                                </>
                              ) : (
                                <span className="text-white-50">-</span>
                              )}
                            </div>
                          </td>

                          <td>
                            {swap.operationType === 'release' && (
                              <span className="badge text-danger border border-danger border-opacity-25 d-inline-flex align-items-center" style={{ backgroundColor: 'rgba(255,0,0,0.1)' }}>
                                <i className="bi bi-box-arrow-right me-1"></i>
                                <span>Svincolo</span>
                              </span>
                            )}
                            {swap.operationType === 'purchase' && (
                              <span className="badge bg-success bg-opacity-10 text-success border border-success border-opacity-25 d-inline-flex align-items-center">
                                <i className="bi bi-box-arrow-in-left me-1"></i>
                                <span>Acquisto</span>
                              </span>
                            )}
                            {swap.operationType === 'swap' && (
                              <span className={`badge ${swap.freeSwap ? 'bg-warning bg-opacity-10 text-warning border border-warning border-opacity-25' : 'bg-info bg-opacity-10 text-info border border-info border-opacity-25'} d-inline-flex align-items-center`}>
                                <i className="bi bi-arrow-left-right me-1"></i>
                                <span>{swap.freeSwap ? 'Scambio Gratuito' : 'Scambio'}</span>
                              </span>
                            )}
                          </td>

                          <td>
                            {swap.wrestlerOut ? (
                              <div className="fw-semibold text-white">{swap.wrestlerOut.name}</div>
                            ) : (
                              <span className="text-white-50">-</span>
                            )}
                          </td>

                          <td className="text-center">
                            <span className={`fw-bold text-brand-secondary ${(swap.scoreOut ?? 0) >= 0 ? 'text-money' : 'text-danger'}`}>
                              {swap.scoreOut !== null ? (swap.scoreOut >= 0 ? `${swap.scoreOut}` : swap.scoreOut) : '-'}
                            </span>
                          </td>

                          <td className="text-center">
                            <span className="text-money fw-bold d-flex align-items-center justify-content-center">
                              {swap.priceOut !== null ? (
                                <>
                                  {swap.priceOut}
                                  <CreditIcon className="ms-1" size={18} />
                                </>
                              ) : '-'}
                            </span>
                          </td>

                          <td>
                            {swap.wrestlerIn ? (
                              <div className="fw-semibold text-white">{swap.wrestlerIn.name}</div>
                            ) : (
                              <span className="text-white-50">-</span>
                            )}
                          </td>

                          <td className="pe-3 text-center">
                            <span className="text-success fw-bold d-flex align-items-center justify-content-center">
                              {swap.priceIn !== null ? (
                                <>
                                  {swap.priceIn}
                                  <CreditIcon className="ms-1" size={18} />
                                </>
                              ) : '-'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="mt-3 d-flex justify-content-between flex-wrap gap-2 px-1">
                <small className="text-white-50">
                  <i className="bi bi-info-circle me-1"></i>
                  Totale operazioni: <span className="fw-semibold">{swaps.length}</span>
                </small>
                <small className="text-white-50">
                  <i className="bi bi-clock me-1"></i>
                  Ultima operazione: {swaps[0]?.date ? new Date(swaps[0].date).toLocaleDateString('it-IT') : '-'}
                </small>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SwapResume;
